import type {
  AppointmentPlanningPreviewPayload,
  AppointmentPlanningPreviewRow,
} from "../../../../../../api/kehrblatt/planning"
import type {
  AppointmentPlanningExistingPlannedRow,
  AppointmentPlanningExistingSourceRow,
  AppointmentPlanningSettingsSnapshot,
  AppointmentPlanningSubmitIntent,
} from "../../../_logic/appointments-contract"
import {
  validateBatchPlanningRowLimit,
  buildAppointmentPlanningCommitPayload,
} from "../_logic/payload"
import { validateDemoBatchPlanningPropertyLimit } from "../../../_logic/demo-batch-planning-limit"
import {
  validatePlanningStartBound,
  type AppointmentPlanningStartBound,
} from "../_shared/_logic/start-bound"

export type BuildAppointmentPlanningSubmitIntentInput = {
  startBound: AppointmentPlanningStartBound
  existingSubmitLabel?: string | null
  sourceLoading: boolean
  sourceError: string | null
  stepLocked: boolean
  rowCount: number
  planningPropertyCount: number
  trialRestrictionsActive?: boolean | null
  startDate: string
  selectedEmployees: string[]
  previewLoading: boolean
  previewCurrent: boolean
  previewError: string | null
  previewPayload: AppointmentPlanningPreviewPayload | null
  effectivePreviewRows: AppointmentPlanningPreviewRow[]
  settings: AppointmentPlanningSettingsSnapshot
  existingPlannedRows: AppointmentPlanningExistingPlannedRow[]
  planningRows: AppointmentPlanningExistingSourceRow[]
  previewByRowId: ReadonlyMap<string, AppointmentPlanningPreviewRow>
  removedRowIds: string[]
}

const blockedIntent = ({
  label,
  message,
  disabled = false,
  disabledReason = null,
}: {
  label: string
  message: string | null
  disabled?: boolean
  disabledReason?: string | null
}): AppointmentPlanningSubmitIntent => ({
  kind: "blocked",
  presentation: { label, disabled, disabledReason },
  message,
})

const commitValidationMessage = ({
  rowCount,
  startDate,
  selectedEmployees,
  previewLoading,
  previewError,
  previewPayload,
}: BuildAppointmentPlanningSubmitIntentInput): string | null => {
  if (!rowCount) return "Keine Aufträge für die Planung gefunden."

  const rowLimitError = validateBatchPlanningRowLimit(rowCount)

  if (rowLimitError) return rowLimitError

  if (!startDate) return "Bitte frühesten Planungstag auswählen."

  if (!selectedEmployees.length)
    return "Bitte mindestens einen rotierenden Mitarbeitenden auswählen."

  if (previewLoading) return "Bitte warten, bis die Ergebnisvorschau berechnet wurde."

  if (previewError) return previewError

  if (!previewPayload) return "Keine Aufträge für die Planung gefunden."

  return null
}

export const buildAppointmentPlanningSubmitIntent = (
  input: BuildAppointmentPlanningSubmitIntentInput,
): AppointmentPlanningSubmitIntent => {
  const label = input.existingSubmitLabel ?? "Termine anlegen"

  if (input.sourceLoading) {
    return blockedIntent({
      label,
      message: null,
      disabled: true,
      disabledReason: "Terminplanungsdaten werden geladen.",
    })
  }

  if (input.sourceError) {
    return blockedIntent({
      label,
      message: null,
      disabled: true,
      disabledReason: input.sourceError,
    })
  }

  if (input.rowCount === 0 && input.removedRowIds.length > 0) {
    return {
      kind: "remove_only",
      presentation: { label, disabled: false, disabledReason: null },
      result: {
        settings: input.settings,
        plannedRows: [...input.existingPlannedRows],
        response: null,
        committed: true,
      },
      removedRowIds: [...input.removedRowIds],
    }
  }

  if (input.stepLocked) {
    return {
      kind: "continue",
      presentation: { label, disabled: false, disabledReason: null },
      result: {
        settings: input.settings,
        plannedRows: [...input.existingPlannedRows],
        response: null,
        committed: true,
      },
    }
  }

  if (input.previewLoading || !input.previewCurrent)
    return blockedIntent({
      label,
      message: null,
      disabled: true,
      disabledReason: input.previewLoading
        ? "Bitte warten, bis die Ergebnisvorschau berechnet wurde."
        : (input.previewError ?? "Die benötigten Terminvorschläge werden automatisch geladen."),
    })

  const validationMessage = commitValidationMessage(input)

  if (validationMessage) return blockedIntent({ label, message: validationMessage })

  const planningLimitError = validateDemoBatchPlanningPropertyLimit(input.planningPropertyCount, {
    trialRestrictionsActive: input.trialRestrictionsActive,
  })

  if (planningLimitError) return blockedIntent({ label, message: planningLimitError })

  const previewPayload = input.previewPayload

  if (!previewPayload)
    return blockedIntent({ label, message: "Keine Aufträge für die Planung gefunden." })

  const readyRowIds = new Set(
    input.effectivePreviewRows.filter((row) => row.commit_target).map((row) => row.client_row_id),
  )

  if (previewPayload.rows.some((row) => !readyRowIds.has(row.client_row_id))) {
    return blockedIntent({
      label,
      message: "Für jede ausgewählte Zeile ist ein vollständiger Terminvorschlag erforderlich.",
      disabled: true,
      disabledReason: "Bitte alle ausgewählten Zeilen vollständig planen.",
    })
  }

  const startError = validatePlanningStartBound(
    previewPayload,
    input.effectivePreviewRows,
    input.startBound,
  )

  if (startError) return blockedIntent({ label, message: startError })

  const payload = buildAppointmentPlanningCommitPayload({
    previewPayload,
    previewRows: input.effectivePreviewRows,
  })
  const presentation = { label, disabled: false, disabledReason: null }

  return {
    kind: "commit",
    settings: input.settings,
    presentation,
    payload,
    rowCount: input.rowCount,
    planningRows: [...input.planningRows],
    previouslyPlannedRows: input.existingPlannedRows.filter((row) =>
      Boolean(row.event || row.preview),
    ),
    previewByRowId: new Map(input.previewByRowId),
    removedRowIds: [...input.removedRowIds],
  }
}
